/**
 * Evaluation settings API (pizza-backend /api/admin/evaluation-settings, developer role only).
 * Hand-written: the committed OpenAPI schema does not cover these endpoints.
 */

export type SettingGroup = "photo_quality" | "doneness" | "pizza_saver" | "extra_ingredients" | (string & {});
export type SettingKind = "threshold" | "flag";
export type SettingValue = number | boolean;
/** the metric fires when value >= threshold ("gte") or value > threshold ("gt") */
export type FailWhen = "gte" | "gt";

export interface EvaluationSetting {
  key: string;
  group: SettingGroup;
  kind: SettingKind;
  type: "float" | "int" | "bool";
  /** effective value: the override, or the default */
  value: SettingValue;
  /** value from the deployment env / model meta */
  default: SettingValue;
  overridden: boolean;
  /** null for flags */
  min: number | null;
  max: number | null;
  step: number | null;
  /** probability and ratio are shown in %, count as a whole number */
  unit: "probability" | "ratio" | "count" | null;
  fail_when: FailWhen | null;
  /** key into DistributionsResponse.metrics; null for flags and thresholds without a stored metric (no histogram) */
  metric: string | null;
  /** flag that switches this threshold's verdict on (true) or to shadow mode (false) */
  enforce_key: string | null;
  /** key of a flag this threshold only matters with */
  requires: string | null;
}

export interface SettingsAuthor {
  user_id: string | null;
  email: string | null;
}

export interface EvaluationSettingsResponse {
  /** 0 = nothing was ever changed (all defaults) */
  version: number;
  updated_at: string | null;
  updated_by: SettingsAuthor | null;
  /** in display order */
  settings: EvaluationSetting[];
}

/** body of PUT: `null` resets a key to its default */
export interface EvaluationSettingsUpdate {
  version: number;
  values: Record<string, SettingValue | null>;
}

export interface SettingsChange {
  key: string;
  old: SettingValue | null;
  new: SettingValue | null;
  /** the change returned the key to its default */
  reset: boolean;
}

export interface SettingsHistoryItem {
  at: string;
  version: number;
  by: SettingsAuthor | null;
  changes: SettingsChange[];
}

export interface SettingsHistoryResponse {
  items: SettingsHistoryItem[];
}

export interface MetricDistribution {
  /** pizzas that have this value */
  n: number;
  lo: number;
  hi: number;
  width: number;
  fail_when: FailWhen;
  /** (hi - lo) / width bins: gte [lo + i*w, lo + (i+1)*w), gt (lo + i*w, lo + (i+1)*w] */
  counts: number[];
  /** values below lo (gte) / <= lo (gt) */
  under: number;
  /** values >= hi (gte) / > hi (gt) */
  over: number;
  /** values exactly 0 (part of counts[0] for gte, of `under` for gt) */
  zero: number;
  quantiles: { p50: number | null; p90: number | null; p99: number | null } | null;
  /** model version that produced the values, when known */
  versions: Record<string, number> | null;
}

export interface DistributionsResponse {
  since: string;
  days: number;
  branch_id: string | null;
  /** pizzas with a stored evaluation in the window */
  pizzas: number;
  metrics: Record<string, MetricDistribution | undefined>;
}

export const DISTRIBUTION_DAYS = [1, 7, 30] as const;
export type DistributionDays = (typeof DISTRIBUTION_DAYS)[number];
